import api from 'configs/api'

export interface NpsEligibilityResponse {
  nps_id?: string
  elegible?: boolean
  eligible?: boolean
}

export interface CreateNpsPayload {
  doctor_id: string
  stars: number
  comment: string
  suggestion: string
}

const NPS_CLOSED_SESSION_KEY = '@StarBem:professionalNPS:closedThisSession'

function parseStoredDoctorId(): string {
  try {
    const raw = window.localStorage.getItem('@StarBem:user')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { id?: string }
    return parsed?.id || ''
  } catch {
    return ''
  }
}

export function getCurrentDoctorId(): string {
  return parseStoredDoctorId()
}

/**
 * Mark that the professional closed the NPS this session (X or outside click).
 * Survey will not show again until the next session (new tab/window or new browser session).
 */
export function setProfessionalNpsClosedThisSession(doctorId: string): void {
  if (!doctorId) return
  try {
    window.sessionStorage.setItem(NPS_CLOSED_SESSION_KEY, 'true')
  } catch {
    // ignore
  }
}

/**
 * True if the professional closed the NPS this session; survey should not open until next session.
 */
export function isProfessionalNpsClosedThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(NPS_CLOSED_SESSION_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Check if the doctor is eligible for NPS survey.
 * GET rating/ms/v1/nps/doctor/:doctor_id
 */
export async function checkDoctorNpsEligible(
  doctorId: string
): Promise<boolean> {
  if (!doctorId) return false

  try {
    const { data } = await api.get<NpsEligibilityResponse>(
      `rating/ms/v1/nps/doctor/${doctorId}`
    )
    return Boolean(data?.elegible ?? data?.eligible)
  } catch {
    return false
  }
}

/**
 * Submit NPS survey.
 * POST rating/ms/v1/nps/doctor
 */
export async function createDoctorNps(
  payload: CreateNpsPayload
): Promise<void> {
  await api.post('rating/ms/v1/nps/doctor', {
    doctor_id: payload.doctor_id,
    stars: payload.stars,
    comment: payload.comment,
    suggestion: payload.suggestion,
  })
}

/**
 * Dismiss NPS (user chose "Não quero responder").
 * POST rating/ms/v1/nps/doctor/dismiss
 */
export async function dismissDoctorNps(doctorId: string): Promise<void> {
  if (!doctorId) return
  await api.post('rating/ms/v1/nps/doctor/dismiss', {
    doctor_id: doctorId,
  })
}
